import { BudgetOnboardingModalLoading } from '@/components/cashflow/budget/BudgetOnboardingModalLoading';
import CategoryIcon from '@/components/cashflow/CategoryIcon';
import { IconButton } from '@/components/common/Button';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import { ChevronDown, ChevronRight } from '@/components/common/icons';
import InputField from '@/components/common/InputField';
import { useErrorToast } from '@/components/common/Toasts';
import { Header, Paragraph, Text } from '@/components/common/Typography';
import useAutofocus from '@/components/common/useAutofocus';
import YesNoSelect from '@/components/common/YesNoSelect';
import { logError } from '@/util/logger';
import { Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import { BudgetFieldsFragment, BudgetPurpose } from '@common/graphql/generated';
import {
    BUDGET_PURPOSE_OPTIONS,
    BudgetCategoryGroupWithAverage,
    CategoryWithAverage,
    useBudgetCategoryGroupsWithAverage,
    useBudgetExpensesFormProps,
    useUpsertBudgetCategoryGroup,
} from '@common/react/budget';
import { TOASTS } from '@common/toasts';
import { toDollars } from '@common/util/currency';
import { useModal } from '@ebay/nice-modal-react';
import { useEffect, useState } from 'react';
import { BudgetAmountForm } from './BudgetAmountForm';
import { useBudgetOnboardingContext } from './BudgetOnboardingContext';
import { BudgetOnboardingLayout } from './BudgetOnboardingLayout';

interface BudgetExpensesFormProps {
    budgetPurpose: Extract<BudgetPurpose, 'fixed' | 'lifestyle'>;
}

export const BudgetExpensesForm = ({
    budgetPurpose,
}: BudgetExpensesFormProps) => {
    const { budget, budgetLoading } = useBudgetOnboardingContext();
    const { budgetCategoryGroups, loading: budgetCategoryGroupsLoading } =
        useBudgetCategoryGroupsWithAverage();

    if (budgetLoading || budgetCategoryGroupsLoading || !budget) {
        return <BudgetOnboardingModalLoading />;
    }

    const filteredBudgetCategoryGroups = budgetCategoryGroups.filter(
        (g) => g.budgetPurpose === budgetPurpose
    );

    return (
        <BudgetExpensesFormInner
            budget={budget}
            budgetCategoryGroups={filteredBudgetCategoryGroups}
            budgetPurpose={budgetPurpose}
        />
    );
};

const BudgetExpensesFormInner = ({
    budget,
    budgetCategoryGroups,
    budgetPurpose,
}: {
    budget: BudgetFieldsFragment;
    budgetCategoryGroups: BudgetCategoryGroupWithAverage[];
} & BudgetExpensesFormProps) => {
    const { goToNextStep, variant } = useBudgetOnboardingContext();
    const showErrorToast = useErrorToast();
    const modal = useModal();

    const { handleSubmit, isSubmitting, amount, setAmount } =
        useBudgetExpensesFormProps({
            budget,
            budgetCategoryGroups,
            budgetPurpose,
            onComplete: () => {
                if (variant === 'create') {
                    goToNextStep();
                } else {
                    modal.remove();
                }
            },
            onError: (e) => {
                logError(e);
                showErrorToast(TOASTS.unexpectedError);
            },
        });

    return (
        <BudgetOnboardingLayout
            onContinue={handleSubmit}
            isContinueDisabled={false}
            isSubmitting={isSubmitting}
            buttonText={
                variant === 'create'
                    ? budgetPurpose === 'fixed'
                        ? 'Go to Lifestyle expenses'
                        : 'Go to Saving & investing'
                    : 'Save'
            }
            onClickEvent={
                variant === 'create'
                    ? budgetPurpose === 'fixed'
                        ? 'Budget Onboarding Go To Lifestyle Expenses Button Pressed'
                        : 'Budget Onboarding Go To Saving & Investing Button Pressed'
                    : 'Budget Expenses Save Button Pressed'
            }
        >
            <Header>
                {budgetPurpose === 'fixed'
                    ? 'Fixed expenses'
                    : 'Lifestyle expenses'}
            </Header>
            <BudgetAmountForm
                amount={amount}
                setAmount={setAmount}
                otherAmount={
                    (budgetPurpose === 'fixed'
                        ? budget.lifestyleAmount
                        : budget.fixedAmount) + budget.savingInvestingAmount
                }
                budgetPurpose={budgetPurpose}
            />
            <FlexCol my={6}>
                <Paragraph>Estimated based on 3-month avg spend</Paragraph>
                <Paragraph fontSize="sm" color="textMuted">
                    Create additional budgets by enabling categories below
                </Paragraph>
            </FlexCol>

            <Table>
                <Thead>
                    <Tr>
                        <Th>Name</Th>
                        <Th>Category</Th>
                        <Th>Amount</Th>
                        <Th w="2%"></Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {budgetCategoryGroups.map((group) => (
                        <CategoryGroupRow
                            key={group.categoryGroup.id}
                            budgetCategoryGroup={group}
                            budgetPurpose={budgetPurpose}
                        />
                    ))}
                </Tbody>
            </Table>
        </BudgetOnboardingLayout>
    );
};

const CategoryGroupRow = ({
    budgetCategoryGroup,
    budgetPurpose,
}: {
    budgetCategoryGroup: BudgetCategoryGroupWithAverage;
    budgetPurpose: BudgetPurpose;
}) => {
    const { autofocusGroupId } = useBudgetOnboardingContext();
    const showErrorToast = useErrorToast();
    const [isExpanded, setIsExpanded] = useState(false);
    const autofocusRef = useAutofocus();
    const shouldAutofocus =
        autofocusGroupId === budgetCategoryGroup.categoryGroup.id;

    useEffect(() => {
        if (shouldAutofocus && autofocusRef?.current) {
            autofocusRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    }, [shouldAutofocus, autofocusRef]);

    const {
        enabled,
        amount,
        budgetPurpose: currentBudgetPurpose,
        updateAttributes,
    } = useUpsertBudgetCategoryGroup({
        budgetCategoryGroup,
        budgetPurpose,
        onError: (e) => {
            logError(e);
            showErrorToast(TOASTS.unexpectedError);
        },
    });

    return (
        <>
            <Tr>
                <Td>
                    <FlexRow gap={5} align="center">
                        <YesNoSelect
                            value={enabled}
                            onChange={() =>
                                updateAttributes({ enabled: !enabled })
                            }
                            variant="flushed"
                            size="sm"
                            autoFocus={false}
                        />
                        <Text>{budgetCategoryGroup.categoryGroup.name}</Text>
                    </FlexRow>
                </Td>
                <Td>
                    <InputField
                        name="budget-purpose"
                        type="fancySelect"
                        onChange={({ value }) => {
                            updateAttributes({ budgetPurpose: value });
                        }}
                        options={BUDGET_PURPOSE_OPTIONS}
                        value={currentBudgetPurpose}
                        fancySelectOptions={{
                            isClearable: false,
                        }}
                    />
                </Td>
                <Td>
                    <InputField
                        ref={shouldAutofocus ? autofocusRef : undefined}
                        name="amount"
                        type="currency"
                        value={amount}
                        onChange={(value) =>
                            updateAttributes({ amount: value })
                        }
                        inputProps={{
                            variant: 'outline',
                            fontSize: 'md',
                            textAlign: 'right',
                        }}
                        inputOptions={{
                            min: 0,
                        }}
                        w="90px"
                    />
                </Td>
                <Td textAlign="right" pr={0}>
                    <IconButton
                        aria-label={isExpanded ? 'Contract' : 'Expanded'}
                        onClickEvent="Expand/Contract Budget Category Group Row Button Clicked"
                        onClick={() => setIsExpanded(!isExpanded)}
                        icon={
                            isExpanded ? (
                                <ChevronDown color="gray.600" size="sm" />
                            ) : (
                                <ChevronRight color="gray.600" size="sm" />
                            )
                        }
                        isRound
                        variant="ghost"
                        size="sm"
                        colorScheme="gray"
                    />
                </Td>
            </Tr>
            {isExpanded &&
                budgetCategoryGroup.categoryAverages.map((category) => (
                    <CategoryRow
                        key={category.category.id}
                        category={category}
                    />
                ))}
        </>
    );
};

const CategoryRow = ({ category }: { category: CategoryWithAverage }) => (
    <Tr>
        <Td>
            <FlexRow align="center" gap={4}>
                <CategoryIcon category={category.category} />
                <Text>{category.category.name}</Text>
            </FlexRow>
        </Td>
        <Td></Td>
        <Td></Td>
        <Td textAlign="right">
            <Text>{toDollars(category.average)}</Text>
        </Td>
    </Tr>
);
